import fetch from './';
import { store } from '../store/store';
import { getAvailableBenchmarkObjects } from 'features/app/redux/context';
import { getSelectedYearForNewGroupReports } from 'features/reports/redux/reports/selectors';

/**
 * Retrieves the reports for a group for all available bennchmark set ids
 * @returns the reports
 */
export const getGroupsReportsByGroupAccessId = () => {
	const state = store.getState();
	const academicYear = getSelectedYearForNewGroupReports(state);
	const availableBenchmarks = getAvailableBenchmarkObjects(state);

	const availableBenchmarkSetIdsQuery = availableBenchmarks
		?.map((benchmark) => `benchmarkSetIds=${benchmark.benchmarkSetId}`)
		.join('&');

	return fetch(
		`/summit/GroupReports/GroupReports/GetReportsForGroup/${academicYear.value}?${availableBenchmarkSetIdsQuery}`,
		{
			credentials: 'same-origin',
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		},
		true
	);
};

/**
 * Checks if there are any new reports available for the group and returns true if yes, no if not
 * @returns the reports
 */
export const areNewReportsForGroupAvailable = (): boolean => {
	const state = store.getState();
	const academicYear = getSelectedYearForNewGroupReports(state);
	const availableBenchmarks = getAvailableBenchmarkObjects(state);

	const availableBenchmarkSetIdsQuery = availableBenchmarks
		?.map((benchmark) => `benchmarkSetIds=${benchmark.benchmarkSetId}`)
		.join('&');

	return fetch(
		`/summit/GroupReports/GroupReports/HasNewReports/${academicYear.value}?${availableBenchmarkSetIdsQuery}`,
		{
			credentials: 'same-origin',
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		},
		true
	);
};

/**
 * Edits the label and bookmarked status of a report bundle
 * @param id the id of the report bundle
 * @param label the label of the report bundle
 * @param isBookmarked the bookmarked status of the report bundle
 * @returns the report bundle
 */
export const editReportBundleLabelAndBookmarkedStatus = (
	id: number,
	label: string,
	isBookmarked: boolean
) => {
	return fetch(
		`/summit/Reports/Reports/EditReport`,
		{
			credentials: 'same-origin',
			method: 'PUT',
			headers: {
				'Content-Type': 'application/json',
			},
			body: {
				BundleId: id,
				Label: label,
				IsBookmarked: isBookmarked,
			},
		},
		true
	);
};

/**
 * Activates the latest version of reports for the selected academic year, regardless of key stage
 * @returns the report request
 */
export const activateLatestReportsRequest = () => {
	const state = store.getState();
	const academicYear = getSelectedYearForNewGroupReports(state);
	const availableBenchmarks = getAvailableBenchmarkObjects(state);
	const availableBenchmarkSetIds = availableBenchmarks?.map(
		(benchmark) => benchmark.benchmarkSetId
	);

	return fetch(
		'/summit/GroupReports/GroupReports/ActivateLatestBundles',
		{
			credentials: 'same-origin',
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: {
				AvailableBenchmarkSetIds: availableBenchmarkSetIds,
				AcademicYear: academicYear.value,
			},
		},
		true
	);
};

/**
 * Notify when a group report is ready
 * @param keyStage - The key stage (KS4 or KS5)
 * @returns Promise<void>
 */
export const notifyGroupReportReady = async (keyStage: 'KS4' | 'KS5'): Promise<void> => {
	return await fetch(`/api/Notification/GroupReportReady/${keyStage}`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
	});
};
